import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Card, CardBody, Col, Row } from 'reactstrap';

import { CardHeaderWithToolbars, Title } from '../../components/Card/CardHeaderWithToolbars';
import FilterContainer from '../../components/Container/FilterContainer/FilterContainer';
import Paginations from '../../components/Paginations';
import privateView from '../../components/hocs/privateView';
import Analytics from '../../services/Analytics';
import { HTTP_STATUS } from '../../constants/kickavenue';
import {
  getCustomerReviewList,
  dismissCustomerReviewError,
  DEFAULT_ERROR_MESSAGE
} from '../../actions/customerReviewAction';
import {
  customerReviewListSelector,
  customerReviewIsFetchSelector,
  customerReviewErrorSelector,
  customerReviewHasRequestedSelector
} from '../../store/selectors/customerReviewSelector';
import Toolbars from './Filters/Toolbars';
import FilterPanel from './customerReviews/FilterPanel';
import List from './customerReviews/List';
import { defaultDraft, toParams, toQuery } from './customerReviewQuery';
import { getCustomerReviewRenderState } from './customerReviewRenderState';

const DETAIL_ROUTE = '/customer_reviews';
const FILTER_PANEL_ID = 'customer_review_filter_panel';

const CustomerReviewIndex = ({ injectedProps, location, match }) => {
  const dispatch = useDispatch();
  const list = useSelector(customerReviewListSelector);
  const isFetch = useSelector(customerReviewIsFetchSelector);
  const error = useSelector(customerReviewErrorSelector);
  const hasRequested = useSelector(customerReviewHasRequestedSelector);

  const [filterOpen, setFilterOpen] = useState(false);
  const [draft, setDraft] = useState(defaultDraft);
  const [params, setParams] = useState(() => toParams(defaultDraft, 1));
  const [reloadToken, setReloadToken] = useState(0);

  const tableRef = useRef(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const analyticsRef = useRef(null);
  analyticsRef.current = { email: injectedProps.email, location, match };

  useEffect(() => {
    return dispatch(getCustomerReviewList({ params: toQuery(params) }));
  }, [dispatch, params, reloadToken]);

  useEffect(() => {
    const { email, location: routeLocation, match: routeMatch } = analyticsRef.current;
    Analytics.recordPageView('customer_review_list', email, {
      url: window.location.href,
      location: routeLocation,
      match: routeMatch
    });
  }, []);

  const reload = useCallback(() => setReloadToken(token => token + 1), []);

  const handleDraftChange = useCallback(({ target: { name, value } }) => {
    setDraft(prevDraft => ({ ...prevDraft, [name]: value }));
  }, []);

  // Show and Sort By apply on change; both return to the first page.
  const handleSortChange = useCallback(({ target: { name, value } }) => {
    const nextDraft = { ...draftRef.current, [name]: value };
    setDraft(nextDraft);
    setParams(toParams(nextDraft, 1));
  }, []);

  // Search and Apply Filter both submit the whole panel, so a keyword search runs
  // within the Status and Source already staged rather than replacing them.
  const handleSubmitPanel = useCallback(e => {
    e.preventDefault();
    setParams(toParams(draftRef.current, 1));
  }, []);

  const handleRefresh = useCallback(e => {
    e.preventDefault();
    reload();
  }, [reload]);

  const handleResetFilter = useCallback(e => {
    e.preventDefault();
    setDraft(defaultDraft);
    setParams(toParams(defaultDraft, 1));
  }, []);

  const handleToggleFilterPanel = useCallback(e => {
    e.preventDefault();
    setFilterOpen(open => !open);
  }, []);

  const handleGoto = useCallback(page => {
    setParams(prevParams => ({ ...prevParams, page }));
  }, []);

  const handleScrollToTable = useCallback(() => {
    if(tableRef.current){
      window.scrollTo({
        top: tableRef.current.offsetTop + 25,
        behavior: 'smooth'
      });
    }
  }, []);

  const hasSnapshot = list.status_code === HTTP_STATUS.OK;
  const renderState = getCustomerReviewRenderState({
    hasRequested,
    isFetch,
    hasSnapshot,
    error
  });

  return (
    <Row>
      <Col xs="12">
        <Card>
          <CardHeaderWithToolbars>
            <Title>
              <i className="fa fa-align-justify"></i>
              <span>Customer Review List</span>
            </Title>
            <Toolbars
              isOpen={filterOpen}
              isLoading={isFetch}
              filterPanelId={FILTER_PANEL_ID}
              onToggleFilter={handleToggleFilterPanel}
              onRefreshList={handleRefresh}
              onResetFilter={handleResetFilter}
            />
          </CardHeaderWithToolbars>
          <CardBody>
            <FilterContainer>
              <FilterPanel
                id={FILTER_PANEL_ID}
                isOpen={filterOpen}
                isLoading={isFetch}
                keyword={draft.keyword}
                status={draft.status}
                source={draft.source}
                per_page={draft.per_page}
                sort={draft.sort}
                onDraftChange={handleDraftChange}
                onSortChange={handleSortChange}
                onSubmitPanel={handleSubmitPanel}
              />
            </FilterContainer>
            <Alert
              color="danger"
              isOpen={renderState.showError}
              toggle={() => dispatch(dismissCustomerReviewError())}
            >
              <span className="mr-2">{ (error && error.message) || DEFAULT_ERROR_MESSAGE }</span>
              <Button
                color="link"
                className="alert-link p-0 align-baseline"
                disabled={isFetch}
                onClick={reload}
              >
                Try Again
              </Button>
            </Alert>
            { renderState.showList && (
              <List
                innerRefs={tableRef}
                list={list}
                isLoading={renderState.showLoadingTable || isFetch}
                detailRoute={DETAIL_ROUTE}
              />
            ) }
            { renderState.showRetryOnly && (
              <Button color="primary" onClick={reload}>Try Again</Button>
            ) }
            { renderState.showPagination && (
              <Paginations
                isLoading={isFetch}
                isFetching={isFetch}
                size="sm"
                lastPage={list.last_page}
                perPage={list.per_page}
                total={list.total}
                maxPage={8}
                path={list.path}
                currentPage={list.current_page}
                prevPageUrl={list.prev_page_url}
                nextPageUrl={list.next_page_url}
                handleGotoCallback={handleGoto}
                onCurrentPageChanged={handleScrollToTable}
              />
            ) }
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

const mapStateToProps = ({ auth: { isFetch, isLogged, roles, id, email } }) => ({
  guardData: { isFetch, isLogged, roles, id, email }
});

export default connect(mapStateToProps)(privateView(CustomerReviewIndex, ['supervisor']));
